import React from "react";
import ReactDOMServer from "react-dom/server";
import PropTypes from "prop-types";

// OwnerCell shows the first owner plus a "+ N other(s)" count, with the full
// list on hover. Falls back to a dash when there are no owners.
//
// When `details` ([{ name, role }]) are supplied (newer payloads) the multi-owner
// hover renders a beautiful role-labeled card via the shared react-tooltip's
// `data-tooltip-html`. Older payloads with only `owners` fall back to the plain
// comma-list `data-tooltip-content`.

const roleLabel = (r) => (r === "finance" ? "Finance" : r === "ops" ? "Operations" : "");

// Group role assignments by person so one owner holding several roles renders as a
// single row with multiple role chips, instead of a repeated name on two rows.
const groupByName = (items) => {
  const order = [];
  const byName = new Map();
  for (const d of items || []) {
    if (!d || !d.name) continue;
    if (!byName.has(d.name)) {
      byName.set(d.name, []);
      order.push(d.name);
    }
    const label = roleLabel(d.role);
    const roles = byName.get(d.name);
    if (label && !roles.includes(label)) roles.push(label);
  }
  return order.map((name) => ({ name, roles: byName.get(name) }));
};

const ownersHtml = (items) =>
  ReactDOMServer.renderToStaticMarkup(
    <div className="min-w-[160px]">
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-2">Owners</div>
      <ul className="space-y-1">
        {groupByName(items).map((g, i) => (
          <li key={i} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-text">{g.name}</span>
            {g.roles.length ? (
              <span className="flex shrink-0 gap-1">
                {g.roles.map((r, j) => (
                  <span
                    key={j}
                    className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-2"
                  >
                    {r}
                  </span>
                ))}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );

const OwnerCell = ({ owners, owner, details, fallback = "—" }) => {
  const hasDetails = Array.isArray(details) && details.length > 0;
  const rawNames = (
    hasDetails ? details.map((d) => d.name) : owners && owners.length ? owners : owner ? [owner] : []
  ).filter(Boolean);
  if (rawNames.length === 0) return <span className="text-text-3">{fallback}</span>;
  // The visible label counts UNIQUE people — the same person owning both the finance
  // and the operations role is one owner, so it reads as just their name (no "+ N other").
  const uniqueNames = [...new Set(rawNames)];
  const extra = uniqueNames.length - 1;
  const label = extra > 0 ? `${uniqueNames[0]} + ${extra} other${extra > 1 ? "s" : ""}` : uniqueNames[0];
  // The tooltip shows one row per role assignment (so a single person with finance +
  // ops still shows two rows), attached whenever there's more than one role row to show
  // (with details) or more than one distinct owner (legacy names-only payload).
  let tipProps = {};
  if (hasDetails && details.length > 1) {
    tipProps = { "data-tooltip-id": "instantTooltip", "data-tooltip-html": ownersHtml(details) };
  } else if (!hasDetails && uniqueNames.length > 1) {
    tipProps = { "data-tooltip-id": "instantTooltip", "data-tooltip-content": uniqueNames.join(", ") };
  }
  const hasTip = !!tipProps["data-tooltip-id"];
  return (
    <span
      className={hasTip ? "cursor-help border-b border-dotted border-border" : undefined}
      {...tipProps}
    >
      {label}
    </span>
  );
};

OwnerCell.propTypes = {
  owners: PropTypes.arrayOf(PropTypes.string),
  owner: PropTypes.string,
  details: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, role: PropTypes.string })
  ),
  fallback: PropTypes.string,
};

export default OwnerCell;
